import { inject, Injectable, NgZone, PLATFORM_ID, signal } from "@angular/core";
import { assets } from "../core/constants/assets.const";
import { BehaviorSubject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../environments/environment";
import { NotificationService } from "./notification.service";
import { ConfirmationService } from "primeng/api";
import { isPlatformBrowser } from "@angular/common";
import { getApps, initializeApp } from "firebase/app";
import { AuthService } from "./auth.service";
import { AppStateService } from "./app-state.service";

@Injectable({
  providedIn: "root",
})
export class MessagingService {
  assetsConst = assets;
  private receivedMessageIds = new Set<string>();
  public showDot = signal<boolean>(false);
  refreshTick = signal(0);
  private messageSource = new BehaviorSubject<any>(null);
  public currentMessage = this.messageSource.asObservable();
  private translate = inject(TranslateService);
  private notificationService = inject(NotificationService);
  private zone = inject(NgZone);
  private confirmationService = inject(ConfirmationService);
  private platformId = inject(PLATFORM_ID);
  private authService = inject(AuthService);
  private appState = inject(AppStateService);

  private tokenSubject = new BehaviorSubject<string | null>(null);
  token$ = this.tokenSubject.asObservable();

  // ─────────────────────────────────────────────────────────────────
  // Safari AudioContext pre-unlock
  // Safari blocks audio.play() without a prior user gesture.
  // We decode the sound file into an AudioBuffer on the first user
  // click (which counts as a gesture) and store the AudioContext.
  // Subsequent playback via createBufferSource() is then allowed
  // even without a gesture — bypassing Safari's autoplay policy.
  // ─────────────────────────────────────────────────────────────────
  private audioCtx: AudioContext | null = null;
  private soundBuffer: AudioBuffer | null = null;
  private audioUnlocked = false;

  // ✅ Keep these getters as-is — they are correct
  private get isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  private get isSafariIOS(): boolean {
    return /iPhone|iPad|iPod/.test(navigator.userAgent);
  }

  private get isSafariMac(): boolean {
    return this.isSafari && !this.isSafariIOS;
  }

  private ensureFirebaseInitialized(): void {
    if (getApps().length === 0) {
      initializeApp(environment.firebaseConfig);
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // ✅ Pre-unlock AudioContext on first user gesture.
  // Called once from initNotificationLogic() via a one-time click
  // listener. Decodes the MP3 into a reusable AudioBuffer so that
  // playSound() works on Safari without a gesture at call time.
  // ─────────────────────────────────────────────────────────────────
  private async unlockAudio(): Promise<void> {
    if (this.audioUnlocked) return;
    this.audioUnlocked = true;

    try {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;

      if (!AudioContextClass) {
        console.warn("[Messaging] AudioContext not available in this browser");
        return;
      }

      this.audioCtx = new AudioContextClass();

      if (this.audioCtx.state === "suspended") {
        await this.audioCtx.resume();

        // console.log(
        //   "[Messaging] AudioContext resumed successfully. State:",
        //   this.audioCtx.state,
        // );
      }

      // ✅ Use absolute URL — relative paths fail when page is on a deep route
      const absoluteUrl = `${window.location.origin}/${this.assetsConst.notificationSound}`;
      const response = await fetch(absoluteUrl);
      const arrayBuffer = await response.arrayBuffer();
      this.soundBuffer = await this.audioCtx.decodeAudioData(arrayBuffer);

      // ✅ Add these debug logs
      // console.log("[Messaging] ✅ AudioContext unlocked successfully");
      // console.log("[Messaging] soundBuffer:", this.soundBuffer);
      // console.log("[Messaging] Sound URL:", this.assetsConst.notificationSound);
    } catch (err) {
      console.error("[Messaging] ❌ Failed to unlock AudioContext:", err);
      this.audioCtx = null;
      this.soundBuffer = null;
    }
  }
  // ─────────────────────────────────────────────────────────────────
  // ✅ Unified sound playback.
  // Prefers AudioContext (works on Safari after unlock) and falls
  // back to new Audio() for Chrome/Firefox/Edge where autoplay
  // restrictions are more relaxed.
  // ─────────────────────────────────────────────────────────────────
  public async playSound(): Promise<void> {
    // console.log("[Messaging] playSound()");

    const userRole = this.appState.user()?.role?.toUpperCase();
    if (userRole === "ADMIN") {
      console.log("[Messaging] Admin user detected. Skipping notification sound.");
      return;
    }

    try {
      if (this.audioCtx?.state === "suspended") {
        console.warn("[Messaging] AudioContext suspended. Resuming...");

        await this.audioCtx.resume();
      }

      if (this.audioCtx && this.soundBuffer) {
        const source = this.audioCtx.createBufferSource();

        source.buffer = this.soundBuffer;

        source.connect(this.audioCtx.destination);

        source.start(0);

        // console.log("[Messaging] Sound played via AudioContext");

        return;
      }
    } catch (err) {
      console.error("[Messaging] AudioContext playback failed", err);
    }

    try {
      const audio = new Audio(
        `${window.location.origin}/${this.assetsConst.notificationSound}`,
      );

      audio.volume = 1;

      await audio.play();
    } catch (err) {
      console.error("[Messaging] Audio fallback failed", err);
    }
  }
  // ─────────────────────────────────────────────────────────────────
  // ✅ FIX: Check Firebase messaging support BEFORE doing anything
  // isSupported() is async and handles Safari 16.4+ correctly
  // ─────────────────────────────────────────────────────────────────
  private async isMessagingSupported(): Promise<boolean> {
    try {
      const { isSupported } = await import("firebase/messaging");
      const supported = await isSupported();
      if (!supported) {
        console.warn(
          supported,
          "[Messaging] Firebase Messaging not supported in this browser/environment.",
        );
      }
      return supported;
    } catch {
      return false;
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // ✅ FIX: Get a VALIDATED SW registration that has pushManager
  // navigator.serviceWorker.ready alone is NOT enough on Safari —
  // it resolves even when pushManager is inaccessible
  // ─────────────────────────────────────────────────────────────────
  private async getSafariSafeSwRegistration(): Promise<ServiceWorkerRegistration | null> {
    if (!("serviceWorker" in navigator)) {
      console.error("[Messaging] Service Workers not supported.");
      return null;
    }

    try {
      // Step 1: Register SW explicitly (idempotent if already registered)
      let registration = await navigator.serviceWorker.getRegistration("/");

      if (!registration) {
        registration = await navigator.serviceWorker.register("/custom-sw.js", {
          scope: "/",
        });
        console.warn("[Messaging] SW registered freshly.");
      }

      // Step 2: Wait for SW to be fully ready
      const readyReg = await navigator.serviceWorker.ready;

      // Step 3: ✅ Critical Safari fix — verify pushManager EXISTS
      // Safari resolves .ready but pushManager can still be undefined
      if (!readyReg.pushManager) {
        console.error(
          "[Messaging] pushManager is undefined — Safari may not support Web Push on this context.",
        );
        return null;
      }

      // Step 4: Wait for SW controller (Safari often has no controller on first load)
      if (!navigator.serviceWorker.controller) {
        console.warn("[Messaging] No SW controller yet, waiting up to 5s...");
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            console.warn(
              "[Messaging] SW controller wait timed out, proceeding anyway.",
            );
            resolve();
          }, 5000);
          navigator.serviceWorker.addEventListener(
            "controllerchange",
            () => {
              clearTimeout(timeout);
              // console.log("[Messaging] SW controller is now active.");
              resolve();
            },
            { once: true },
          );
        });
      }

      return readyReg;
    } catch (err) {
      console.error("[Messaging] getSafariSafeSwRegistration failed:", err);
      return null;
    }
  }

  // ─────────────────────────────────────────────────────────────────
  // ✅ FIX: initNotificationLogic is now async so it can await
  // isMessagingSupported() before ANY Firebase or SW code runs.
  // Also registers a one-time click handler to pre-unlock the
  // AudioContext for Safari sound playback.
  // ─────────────────────────────────────────────────────────────────
  public async initNotificationLogic(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !("Notification" in window))
      return;

    // ✅ Try to unlock AudioContext immediately — Chrome often allows
    // AudioContext creation without a gesture if the user has visited
    // the site before (Media Engagement Index). If it fails or stays
    // suspended, the click handler below will retry on first interaction.
    this.unlockAudio().catch(() => {});

    // ✅ Backup: re-attempt unlock on first user click (covers Safari
    // and fresh Chrome sessions where autoplay is blocked).
    document.addEventListener(
      "click",
      () => {
        if (!this.audioUnlocked || !this.soundBuffer) {
          this.audioUnlocked = false; // reset so unlockAudio() reruns
          this.unlockAudio().catch(() => {});
        }
      },
      { once: true, passive: true },
    );

    // ✅ MUST check support FIRST — before ensureFirebaseInitialized()
    // This is what prevents the messaging/unsupported-browser error on Safari
    const supported = await this.isMessagingSupported();
    if (!supported) return;

    this.ensureFirebaseInitialized();

    const permission = Notification.permission;
    const hasAskedPermission =
      localStorage.getItem("hasAskedNotificationPermission") === "true";

    // console.log(
    //   "[Messaging] Permission:",
    //   permission,
    //   "| isSafari:",
    //   this.isSafari,
    //   "| isSafariIOS:",
    //   this.isSafariIOS,
    // );

    if (permission === "granted") {
      await this.requestPermission();
    } else if (permission === "default") {
      if (this.isSafari) {
        this.registerSafariClickHandler();
      } else if (!hasAskedPermission) {
        this.showPermissionDialog(false);
      }
    } else if (permission === "denied") {
      if (!hasAskedPermission) {
        this.showPermissionDialog(true);
        localStorage.setItem("hasAskedNotificationPermission", "true");
      }
    }

    // ✅ FIX: Only attach foreground listener if messaging is supported
    // (we already confirmed it above) — and only after SW is likely ready
    this.listenForFrontMessages();
  }

  private registerSafariClickHandler(): void {
    const triggerSafariPermission = () => {
      document.removeEventListener("click", triggerSafariPermission);

      let resolved = false;
      const handlePermissionResult = (permission: string) => {
        if (resolved) return;
        resolved = true;
        // console.log("[Messaging] Safari permission result:", permission);

        localStorage.setItem("hasAskedNotificationPermission", "true");

        if (permission === "granted") {
          localStorage.removeItem("hasAskedNotificationPermission");
          this.requestPermission();
        }
      };

      try {
        const result = (Notification as any).requestPermission(
          handlePermissionResult,
        );
        if (result && typeof result.then === "function") {
          result
            .then(handlePermissionResult)
            .catch(() => handlePermissionResult(Notification.permission));
        }
      } catch (err) {
        console.error("[Messaging] Safari requestPermission error:", err);
        handlePermissionResult(Notification.permission);
      }
    };

    document.addEventListener("click", triggerSafariPermission);
  }

  private showPermissionDialog(isDenied: boolean): void {
    const getDeniedMessage = (): string => {
      if (this.isSafariIOS) {
        return this.translate.instant(
          "Notifications are blocked. Go to Settings → Safari → Notifications and allow this site.",
        );
      } else if (this.isSafariMac) {
        return this.translate.instant(
          "Notifications are blocked. Go to Safari → Settings → Websites → Notifications and allow this site.",
        );
      } else if (/firefox/i.test(navigator.userAgent)) {
        return this.translate.instant(
          "Notifications are blocked. Click the lock icon in the address bar → Permissions → Notifications → Allow.",
        );
      } else if (/edg/i.test(navigator.userAgent)) {
        return this.translate.instant(
          "Notifications are blocked. Click the lock icon in the address bar → Permissions for this site → Notifications → Allow.",
        );
      } else {
        return this.translate.instant(
          "Notifications are blocked. Click the lock icon in the address bar → Site settings → Notifications → Allow.",
        );
      }
    };

    this.translate
      .get(["notificationAlert", "enableNotifications", "yes", "no"])
      .subscribe(() => {
        this.confirmationService.confirm({
          key: "notifications",
          message: isDenied
            ? getDeniedMessage()
            : this.translate.instant(
                "Please enable browser notifications to receive important updates.",
              ),
          header: this.translate.instant("Enable Notifications"),
          icon: "pi pi-bell",
          acceptLabel: isDenied
            ? this.translate.instant("Ok")
            : this.translate.instant("Yes"),
          rejectLabel: isDenied ? undefined : this.translate.instant("No"),
          accept: () => {
            localStorage.setItem("hasAskedNotificationPermission", "true");
            if (!isDenied) {
              Notification.requestPermission().then((permission) => {
                if (permission === "granted") {
                  localStorage.removeItem("hasAskedNotificationPermission");
                  this.requestPermission();
                }
              });
            }
          },
          reject: () => {
            localStorage.setItem("hasAskedNotificationPermission", "true");
          },
        });
      });
  }

  async requestPermission(): Promise<void> {
    if (this.isSafari) {
      const swReg = await navigator.serviceWorker.getRegistration("/");
      // console.log(
      //   "[Safari] SW registration scope:",
      //   swReg?.scope,
      //   "| state:",
      //   swReg?.active?.state,
      // );
    }

    const storedToken = localStorage.getItem("fcmToken");
    const syncedToken = localStorage.getItem("fcmTokenSynced");

    if (storedToken) {
      // console.log("[Messaging] Using existing token:", storedToken);
      this.tokenSubject.next(storedToken);

      if (this.authService.isAuthenticated() && syncedToken !== storedToken) {
        this.syncTokenWithBackend(storedToken);
        localStorage.setItem("fcmTokenSynced", storedToken);
      }
      return;
    }

    this.ensureFirebaseInitialized();

    try {
      // ✅ FIX: Use the new safe registration helper instead of
      // navigator.serviceWorker.ready directly — this validates pushManager
      const registration = await this.getSafariSafeSwRegistration();

      if (!registration) {
        console.error(
          "[Messaging] Aborting getToken — no valid SW registration.",
        );
        return;
      }

      const { getMessaging, getToken } = await import("firebase/messaging");
      const messaging = getMessaging();

      // console.log("[Messaging] No token in storage. Calling getToken...");

      const currentToken = await getToken(messaging, {
        vapidKey: environment.firebaseConfig.vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!currentToken) {
        console.warn("[Messaging] getToken returned empty.");
        return;
      }

      // console.log("[Messaging] FCM Token generated:", currentToken);
      localStorage.setItem("fcmToken", currentToken);
      this.tokenSubject.next(currentToken);

      if (this.authService.isAuthenticated()) {
        this.syncTokenWithBackend(currentToken);
      }
    } catch (err) {
      console.error("[Messaging] Error getting FCM token:", err);
      if (err instanceof Error) {
        console.error("[Messaging]", err.name, "|", err.message);
      }
    }
  }

  private syncTokenWithBackend(token: string): void {
    // console.log("[Messaging] Syncing FCM token with backend...");
    this.authService.updateDeviceToken(token).subscribe({
      next: () =>
        console.warn("[Messaging] Token synced with backend successfully."),
      error: (err) =>
        console.error("[Messaging] Failed to sync token with backend:", err),
    });
  }

  async listenForFrontMessages(): Promise<void> {
    try {
      const { getMessaging, onMessage } = await import("firebase/messaging");
      const messaging = getMessaging();

      console.warn("[Messaging] Attaching foreground onMessage listener...");

      onMessage(messaging, (payload: any) => {
        // console.log("[Messaging] ✅ Foreground message received:", payload);

        const messageId = payload.messageId;

        if (!messageId) {
          console.warn(
            "[Messaging] Foreground message has no messageId, skipping.",
          );
          return;
        }

        if (this.receivedMessageIds.has(messageId)) {
          return;
        }

        this.receivedMessageIds.add(messageId);

        this.zone.run(() => {
          if (payload.notification) {
            // console.log(
            //   "[Messaging] Notification payload found, playing sound...",
            // );
            this.playSound();

            new Notification(
              payload.notification["title"] || "New Notification",
              {
                body: payload.notification["body"] || "Click to view",
                icon: this.assetsConst.logo,
                data: payload.notification,
              },
            );

            this.sendMessage({ action: "refresh" });
          }
        });
      });
    } catch (err) {
      console.error(
        "[Messaging] Failed to attach foreground message listener:",
        err,
      );
    }
  }

  sendMessage(message: any): void {
    this.messageSource.next(message);
  }

  private displayNotification(
    title: string,
    options: NotificationOptions,
  ): void {
    navigator.serviceWorker.ready
      .then((registration) => {
        if (registration) {
          registration.showNotification(title, options);
        } else {
          console.error(
            "[Messaging] SW registration not found for notification display.",
          );
        }
      })
      .catch((err) => {
        console.error("[Messaging] displayNotification failed:", err);
      });
  }
}
